// Client-side types + display helpers for en-bloc (collective-sale) insights.
// The scoring, gathering and persistence all live in the Worker; this mirrors
// the JSON it returns and adds presentation helpers. No user data is sent — the
// per-project lookup is opt-in and carries only a public building name.

export interface EnblocIndicator {
  key: string;
  label: string;
  present: boolean;
  sub: number | null; // 0..1 sub-score
  weight: number;
  contribution: number | null;
  value?: string;
  predictiveness?: number | null; // learned hit-rate from real outcomes, when available
  predictivenessN?: number | null;
}

export interface EnblocHeadline {
  title: string;
  url: string;
  source?: string;
  date?: string;
  kind: "committee" | "tender" | "reserve" | "sold" | "failed" | "attempt" | "nearby" | "news";
}

export type EnblocBand = "high" | "moderate" | "low" | "insufficient";

export interface EnblocInsight {
  projectKey: string;
  projectName: string;
  district?: string | null;
  score: number | null;
  band: EnblocBand;
  confidencePct: number;
  coverage: number;
  indicators: EnblocIndicator[];
  headlines: EnblocHeadline[];
  summary: string;
  outcome: "unknown" | "watch" | "attempt" | "failed" | "sold";
  outcomeNote?: string | null;
  outcomeDate?: string | null;
  dataSource: "firecrawl" | "cache" | "heuristic";
  firstSeen: string;
  lastRefreshed: string;
}

export interface EnblocDigest {
  district: string;
  projectsTracked: number;
  headlines: EnblocHeadline[];
  configured: boolean;
}

// A compact snapshot persisted on a shortlisted item (in the encrypted local
// vault) so a fetched insight is remembered for future reference on the client
// too, without re-querying.
export interface EnblocSnapshot {
  score: number | null;
  band: EnblocBand;
  confidencePct: number;
  outcome: EnblocInsight["outcome"];
  headlineCount: number;
  fetchedAt: string;
  lastRefreshed: string;
}

export function bandLabel(band: EnblocBand): string {
  return band === "high" ? "High en-bloc potential"
    : band === "moderate" ? "Moderate en-bloc potential"
    : band === "low" ? "Low en-bloc potential"
    : "Not enough data yet";
}

export function bandColor(band: EnblocBand): string {
  return band === "high" ? "var(--good)"
    : band === "moderate" ? "var(--neutral)"
    : band === "low" ? "var(--muted)"
    : "var(--muted)";
}

export function outcomeLabel(o: EnblocInsight["outcome"]): string | null {
  return o === "sold" ? "Sold en bloc"
    : o === "failed" ? "Past attempt lapsed"
    : o === "attempt" ? "Attempt in progress"
    : o === "watch" ? "On the radar"
    : null;
}

const KIND_LABEL: Record<EnblocHeadline["kind"], string> = {
  committee: "Committee", tender: "Tender", reserve: "Reserve price",
  sold: "Sold", failed: "Lapsed", attempt: "Attempt", nearby: "Nearby", news: "News",
};
export function headlineKindLabel(k: EnblocHeadline["kind"]): string {
  return KIND_LABEL[k] ?? "News";
}

// Extract a "Dxx" district code from a shortlist item's locality label, e.g.
// "D15 Katong / Marine Parade / East Coast" → "D15".
export function districtCode(locality?: string | null): string | null {
  const m = /\bD\s?(\d{1,2})\b/i.exec(locality ?? "");
  return m ? `D${m[1].padStart(2, "0")}` : null;
}

export function snapshotOf(ins: EnblocInsight): EnblocSnapshot {
  return {
    score: ins.score,
    band: ins.band,
    confidencePct: ins.confidencePct,
    outcome: ins.outcome,
    headlineCount: ins.headlines.length,
    fetchedAt: new Date().toISOString(),
    lastRefreshed: ins.lastRefreshed,
  };
}
